/**
 * useSubscription Hook
 * React hook for accessing user subscription and feature permissions
 */

import { useState, useEffect } from 'react';
import SubscriptionService from '../services/subscriptionService';
import type { FeaturePermissions, PlanType } from '../services/subscriptionService';

interface UseSubscriptionResult {
  permissions: FeaturePermissions | null;
  planType: PlanType | null;
  isLoading: boolean;
  isStandardPlan: boolean;
  hasFullAccess: boolean;
  refresh: () => Promise<void>;
}

export const useSubscription = (): UseSubscriptionResult => {
  const [permissions, setPermissions] = useState<FeaturePermissions | null>(null);
  const [planType, setPlanType] = useState<PlanType | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const subscriptionService = SubscriptionService.getInstance();

  const loadSubscription = async () => {
    setIsLoading(true);
    try {
      const [perms, plan] = await Promise.all([
        subscriptionService.getFeaturePermissions(),
        subscriptionService.getPlanType()
      ]);
      setPermissions(perms);
      setPlanType(plan);
    } catch (error) {
      console.error('[useSubscription] Error loading subscription:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const refresh = async () => {
    subscriptionService.clearCache();
    await loadSubscription();
  };

  useEffect(() => {
    loadSubscription();
  }, []);

  return {
    permissions,
    planType,
    isLoading,
    isStandardPlan: planType === 'standard',
    hasFullAccess: permissions?.hasFullAccess || false,
    refresh
  };
};
